
import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  color: string;
  size: string;
  quantity: number;
  price: number;
  image: string;
  maxStock: number;
}

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onClearCart: () => void;
  onClose: () => void;
  onCompleteOrder: (orderData: {
    items: CartItem[];
    customerName: string;
    customerPhone: string;
    customerAddress: string;
    notes: string;
    totalAmount: number;
  }) => void;
}

export default function Cart({
  items = [], // Thêm default value
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onClose,
  onCompleteOrder
}: CartProps) {
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userProfile } = useAuth();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const getTotalAmount = () => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getTotalItems = () => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (!items || !Array.isArray(items)) return;
    
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    if (newQuantity <= 0) {
      if (confirm('Bạn có muốn xóa sản phẩm này khỏi giỏ hàng?')) {
        onRemoveItem(itemId);
      }
      return;
    }

    if (newQuantity > item.maxStock) {
      alert(`Số lượng tối đa cho sản phẩm này là ${item.maxStock}`);
      return;
    }

    onUpdateQuantity(itemId, newQuantity);
  };

  const validateCustomerInfo = () => {
    if (!customerInfo.customerName.trim()) {
      alert('Vui lòng nhập tên khách hàng!');
      return false;
    }
    if (!customerInfo.customerPhone.trim()) {
      alert('Vui lòng nhập số điện thoại!');
      return false;
    }
    if (!customerInfo.customerAddress.trim()) {
      alert('Vui lòng nhập địa chỉ giao hàng!');
      return false;
    }
    
    // Validate phone number format
    const phoneRegex = /^[0-9]{10,11}$/;
    if (!phoneRegex.test(customerInfo.customerPhone.replace(/\s/g, ''))) {
      alert('Số điện thoại không hợp lệ! Vui lòng nhập 10-11 chữ số.');
      return false;
    }
    
    return true;
  };

  const handleCompleteOrder = async () => {
    if (!items || items.length === 0) {
      alert('Giỏ hàng trống. Vui lòng thêm sản phẩm trước khi đặt hàng!');
      return;
    }

    if (!validateCustomerInfo()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const orderData = {
        items,
        ...customerInfo,
        totalAmount: getTotalAmount()
      };

      await onCompleteOrder(orderData);
      
      // Reset form after successful order
      setCustomerInfo({
        customerName: '',
        customerPhone: '',
        customerAddress: '',
        notes: ''
      });
      setShowCheckout(false);
    } catch (error) {
      console.error('Error completing order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showCheckout) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Hoàn thành đơn hàng</h2>
            <button
              onClick={() => setShowCheckout(false)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {/* Tóm tắt đơn hàng */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tóm tắt đơn hàng</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-60 overflow-y-auto">
                {items && items.length > 0 ? items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <img
                        src={item.image}
                        alt={item.productName}
                        className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm truncate">{item.productName}</p>
                        <p className="text-xs text-gray-600">
                          {item.color} - {item.size} × {item.quantity}
                        </p>
                      </div>
                    </div>
                    <p className="font-medium text-gray-900 text-sm flex-shrink-0 ml-2">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                )) : (
                  <p className="text-gray-500 text-center">Không có sản phẩm nào</p>
                )}
                <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Tổng cộng:</span>
                  <span className="font-bold text-orange-600 text-lg">
                    {formatCurrency(getTotalAmount())}
                  </span>
                </div>
              </div>
            </div>

            {/* Thông tin khách hàng */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Thông tin khách hàng</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên khách hàng <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customerInfo.customerName}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, customerName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                    placeholder="Nhập tên khách hàng"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số điện thoại <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.customerPhone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, customerPhone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                    placeholder="Nhập số điện thoại (10-11 chữ số)"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Địa chỉ giao hàng <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={customerInfo.customerAddress}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, customerAddress: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base resize-none"
                    placeholder="Nhập địa chỉ giao hàng đầy đủ"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ghi chú
                  </label>
                  <textarea
                    value={customerInfo.notes}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base resize-none"
                    placeholder="Ghi chú thêm cho đơn hàng (tùy chọn)"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <span className="text-gray-600 text-sm sm:text-base">Tổng đơn hàng: </span>
                <span className="font-bold text-orange-600 text-lg sm:text-xl">
                  {formatCurrency(getTotalAmount())}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setShowCheckout(false)}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Quay lại
                </button>
                <button
                  onClick={handleCompleteOrder}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-200 whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Đang xử lý...
                    </div>
                  ) : (
                    'Hoàn thành đơn hàng'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Giỏ hàng ({getTotalItems()})
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!items || items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shopping-cart-line text-2xl text-gray-400"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Giỏ hàng trống</h3>
              <p className="text-gray-600 text-center text-sm">
                Thêm sản phẩm vào giỏ hàng để bắt đầu mua sắm
              </p>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow"
                >
                  <img
                    src={item.image}
                    alt={item.productName}
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate text-sm">
                      {item.productName}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Màu: {item.color} | Size: {item.size}
                    </p>
                    <p className="text-sm font-medium text-orange-600 mt-1">
                      {formatCurrency(item.price)}
                    </p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <i className="ri-subtract-line text-xs"></i>
                      </button>
                      <span className="w-8 text-center text-sm font-medium">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        disabled={item.quantity >= item.maxStock}
                        className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed rounded-full transition-colors"
                      >
                        <i className="ri-add-line text-xs"></i>
                      </button>
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-1">
                      Tồn kho: {item.maxStock} | Thành tiền: {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      if (confirm('Bạn có muốn xóa sản phẩm này khỏi giỏ hàng?')) {
                        onRemoveItem(item.id);
                      }
                    }}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items && items.length > 0 && (
          <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            {/* Total */}
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-600 font-medium">Tổng cộng:</span>
              <span className="text-xl font-bold text-orange-600">
                {formatCurrency(getTotalAmount())}
              </span>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={() => setShowCheckout(true)}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap"
              >
                Tiến hành đặt hàng
              </button>
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (confirm('Bạn có chắc chắn muốn xóa tất cả sản phẩm trong giỏ hàng?')) {
                      onClearCart();
                    }
                  }}
                  className="flex-1 border border-red-300 text-red-600 hover:bg-red-50 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  Xóa tất cả
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  Tiếp tục mua
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}